/* =========================================================
   AUTH STORAGE (JWT)
   ========================================================= */

const TOKEN_KEY = 'admin.accessToken.session';
const ROLE_KEY = 'admin.role.session';

let mem = '';
let _refreshTimer = null;

/* =========================================================
   TOKEN STORAGE
   ========================================================= */

/**
 * Зберігаємо accessToken
 * приймає:
 *  - "xxx"
 *  - "Bearer xxx"
 */
export function saveToken(t) {
    if (!t) return clearToken();

    const m = String(t).match(/^Bearer\s+(.+)$/i);
    const clean = m ? m[1].trim() : String(t).trim();

    mem = clean;

    try {
        sessionStorage.setItem(TOKEN_KEY, clean);
    } catch {}

    scheduleRefresh();
}
export const setToken = saveToken;

/** Отримати токен */
export function getToken() {
    try {
        const v = sessionStorage.getItem(TOKEN_KEY);
        if (v && v !== 'undefined' && v !== 'null') return v;
    } catch {}
    return mem || '';
}

/** Очистити токен (і роль) */
export function clearToken() {
    mem = '';
    cancelRefresh();
    try {
        sessionStorage.removeItem(TOKEN_KEY);
    } catch {}
    clearRole();
}

/** Чи є токен */
export function isAuthed() {
    const t = getToken();
    return !!(t && t !== 'undefined' && t !== 'null');
}


/* =========================================================
   ROLE STORAGE
   ========================================================= */

export function saveRole(role) {
    try {
        sessionStorage.setItem(ROLE_KEY, String(role || ''));
    } catch {}
}

export function getRole() {
    try {
        return sessionStorage.getItem(ROLE_KEY) || '';
    } catch {}
    return '';
}

function clearRole() {
    try {
        sessionStorage.removeItem(ROLE_KEY);
    } catch {}
}

export function isSuperAdmin() {
    return getRole() === 'superadmin';
}


/* =========================================================
   ENV / API BASE
   ========================================================= */

export function getApiBase() {
    const base = process.env.REACT_APP_API_URL;

    if (!base) {
        throw new Error('REACT_APP_API_URL is not configured');
    }

    return base.replace(/\/+$/, '');
}


/* =========================================================
   TOKEN DECODE (без верифікації підпису)
   ========================================================= */

function getTokenExp(token) {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
    } catch {
        return null;
    }
}


/* =========================================================
   REFRESH LOGIC
   ========================================================= */

function cancelRefresh() {
    if (_refreshTimer !== null) {
        clearTimeout(_refreshTimer);
        _refreshTimer = null;
    }
}

async function doRefresh() {
    _refreshTimer = null;

    try {
        const base = getApiBase();
        const res = await fetch(`${base}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
        });

        if (!res.ok) {
            clearToken();
            return;
        }

        const data = await res.json();
        if (data?.accessToken) {
            saveToken(data.accessToken); // рекурсивно планує наступний refresh
        } else {
            clearToken();
        }

    } catch {
        // мережева помилка — retry через 30 сек
        _refreshTimer = setTimeout(doRefresh, 30_000);
    }
}

function scheduleRefresh() {
    cancelRefresh();

    const token = getToken();
    if (!token) return;

    const exp = getTokenExp(token);
    if (!exp) return;

    const delay = exp - Date.now() - 60_000; // за 1 хв до закінчення

    if (delay <= 0) {
        doRefresh();
        return;
    }

    _refreshTimer = setTimeout(doRefresh, delay);
}

/** Для api.js — спроба оновити токен, повертає true/false */
export async function tryRefresh() {
    try {
        const base = getApiBase();
        const res = await fetch(`${base}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
        });

        if (!res.ok) {
            clearToken();
            return false;
        }

        const data = await res.json();
        if (data?.accessToken) {
            saveToken(data.accessToken);
            return true;
        }

        clearToken();
        return false;

    } catch {
        return false;
    }
}


/* =========================================================
   INIT AUTH (при старті адмінки)
   ========================================================= */

/**
 * Нормальний режим:
 * читає sessionStorage, планує refresh якщо є токен
 */
export function initAuth() {
    getToken();
    if (isAuthed()) {
        scheduleRefresh();
    }
}

/**
 * DEV режим:
 * примусовий логін при кожному відкритті адмінки
 */
export function initAuthForceLoginOnLoad() {
    clearToken();
}


/* =========================================================
   LOGIN FLOW HELPERS
   ========================================================= */

/**
 * Обробка успішного логіну
 * приймає { accessToken, role }
 */
export function handleLoginSuccess(data) {
    if (!data?.accessToken) {
        throw new Error('No accessToken returned');
    }

    saveToken(data.accessToken);
    if (data.role) saveRole(data.role);
}

/**
 * Logout — клірить cookie на бекенді і видаляє токен
 */
export async function logout() {
    try {
        const base = getApiBase();
        await fetch(`${base}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
        });
    } catch {}

    clearToken();
    window.location.assign('/login');
}


/* =========================================================
   ROUTE GUARD HELPERS
   ========================================================= */

/**
 * Редірект якщо не авторизований
 */
export function requireAuth() {
    if (!isAuthed()) {
        const here = window.location.pathname + window.location.search;

        const url = new URL('/login', window.location.origin);
        url.searchParams.set('from', here);

        window.location.assign(url.toString());
        return false;
    }

    return true;
}

/**
 * Якщо вже залогінений — не пускаємо на login
 */
export function redirectIfAuthed() {
    if (isAuthed()) {
        window.location.assign('/');
        return true;
    }
    return false;
}
